// Views/NewCaseView/NewCase.js
import { Button, Dialog, makeStyles } from "@material-ui/core";
import { Form, Formik } from "formik";
import React, { useRef, useState } from "react";

import SmartToyIcon from "@mui/icons-material/SmartToy";

import CustomLoader from "../../components/CustomLoader";
import NewCaseTopBar from "../../components/NewCaseView/NewCaseTopBar";
import CaseClientInfoTab from "../../components/NewCaseView/CaseClientInfoTab";
import CaseDetailsTab from "../../components/NewCaseView/CaseDetailsTab";
import NewCaseSubjectForm from "../../components/NewCaseView/NewCaseSubjectForm";
import { useCaseStyles } from "../../components/NewCaseView/useCaseStyle";

import ValidationSchema from "../../components/shared/helpers/NewCaseValidationSchema";

// Import custom hooks
import { useCaseData } from "../../hooks/NewCaseView/useCaseData";
import { useCaseSubmission } from "../../hooks/NewCaseView/useCaseSubmission";
import { useFormInitialization } from "../../hooks/NewCaseView/useFormInitialization";
import AIChatDrawer from "../../components/AIChatbot/AIChatDrawer";
import { tokens } from "../../theme/tokens";

const useStyle = makeStyles(() => ({
  // "Create with AI" — a secondary control, quiet next to the one primary
  // action (Next/Submit): bordered surface, no shadow, no hover movement.
  aiButton: {
    backgroundColor: tokens.color.surface.default,
    color: tokens.color.text.primary,
    border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    borderRadius: tokens.radius.sm,
    padding: `${tokens.space[2]} ${tokens.space[3]}`,
    textTransform: "none",
    fontFamily: tokens.type.family,
    fontSize: tokens.type.dense.size,
    fontWeight: tokens.type.denseStrong.weight,
    height: tokens.density.field,
    boxShadow: "none",
    whiteSpace: "nowrap",
    "&:hover": {
      backgroundColor: tokens.color.surface.hover,
      boxShadow: "none",
    },
    "& .MuiButton-startIcon": {
      marginRight: tokens.space[2],
    },
  },
  aiButtonActive: {
    backgroundColor: tokens.color.brand.primaryAction,
    color: tokens.color.brand.onPrimary,
    borderColor: tokens.color.brand.primaryAction,
    "&:hover": {
      backgroundColor: tokens.color.brand.primaryActionHover,
    },
  },
  aiIcon: {
    fontSize: tokens.icon.default,
  },
}));

function NewCase() {
  const classes = useStyle();
  const wizard = useCaseStyles();
  const formRef = useRef();

  // Local state
  const [subjectIndex, setSubjectIndex] = useState(0);
  const [tabIndex, setTabIndex] = useState(0);
  const [aiDrawerOpen, setAIDrawerOpen] = useState(false);

  // Custom hooks
  const {
    clientList,
    clientSelect,
    fieldResourceSelect,
    loading: dataLoading,
  } = useCaseData();
  const { submitCase, isSubmitting } = useCaseSubmission();
  const { initialValues } = useFormInitialization();

  /**
   * Handle date referred change and auto-calculate due date
   */
  const handleDateRefferedChange = (e) => {
    formRef.current.setFieldValue(
      "case_details.case_information.date_referred",
      e.target.value,
      false,
    );
    const date = new Date(e.target.value);
    date.setDate(date.getDate() + 30);
    formRef.current.setFieldValue(
      "case_details.case_information.due_date",
      `${date.toISOString().split("T")[0]}`,
      false,
    );
  };

  /**
   * Check if should submit or go to next step
   */
  const submitCheck = async (values) => {
    console.log("submit ", tabIndex);
    if (tabIndex < 2) {
      setTabIndex(tabIndex + 1);
    } else {
      await submitCase(values, clientList);
    }
  };

  return (
    <div className={wizard.pageWrap}>
      <div className={wizard.panel}>
        <div className={wizard.panelHeader}>
          <NewCaseTopBar tabIndex={tabIndex} />
          {/* AI Button */}
          <Button
            startIcon={<SmartToyIcon className={classes.aiIcon} />}
            className={`${classes.aiButton} ${
              aiDrawerOpen ? classes.aiButtonActive : ""
            }`}
            onClick={() => setAIDrawerOpen(true)}
          >
            Create with AI
          </Button>
        </div>

        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          validationSchema={ValidationSchema[tabIndex]}
          validateOnBlur={false}
          validateOnChange
          onSubmit={(values) => {
            submitCheck(values);
          }}
          innerRef={formRef}
          render={({
            // isSubmitting,
            dirty,
            touched,
            values,
            errors,
            setFieldValue,
          }) => (
            <Form>
              <div className={wizard.panelBody}>
                {tabIndex === 0 ? (
                  <>
                    <CaseClientInfoTab
                      clientList={clientList}
                      clientSelect={clientSelect}
                      fieldResourceSelect={fieldResourceSelect}
                      setFieldValue={setFieldValue}
                      values={values}
                    />
                  </>
                ) : null}

                {tabIndex === 1 ? (
                  <>
                    <CaseDetailsTab
                      values={values}
                      setFieldValue={setFieldValue}
                      handleDateRefferedChange={handleDateRefferedChange}
                    />
                  </>
                ) : null}

                {tabIndex === 2 ? (
                  <>
                    <NewCaseSubjectForm
                      subjects={values.case_details.Subjects_information}
                      currentIndex={subjectIndex}
                      changeIndex={setSubjectIndex}
                      errors={errors}
                      setFieldValue={setFieldValue}
                    />
                  </>
                ) : null}
              </div>

              <div className={wizard.footerBar}>
                {tabIndex > 0 ? (
                  <Button
                    className={wizard.backButton}
                    disabled={tabIndex === 0}
                    onClick={(e) => {
                      setTabIndex(tabIndex - 1);
                    }}
                  >
                    Back
                  </Button>
                ) : (
                  <span />
                )}

                <Button
                  type={"submit"}
                  className={wizard.postButton}
                  disabled={isSubmitting}
                >
                  {tabIndex < 2 ? "Next" : "Submit"}
                </Button>

                <Dialog open={isSubmitting || dataLoading}>
                  {isSubmitting || dataLoading ? <CustomLoader /> : null}
                </Dialog>
              </div>
            </Form>
          )}
        />
      </div>
      {/* AI Chat Drawer */}
      <AIChatDrawer
        open={aiDrawerOpen}
        onClose={() => setAIDrawerOpen(false)}
      />
    </div>
  );
}

export default NewCase;
